import * as yup from 'yup';
import type { AnyObject } from 'yup';
import { LocalizeNameSpaceTypes } from '@gov-nx/core/service/translations/name-space.types';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import type { Maybe } from '@gov-nx/core/types/helpers.types';
import type { FormInputProps } from '@gov-nx/ui/types/forms/FormInput';
import { STREET_NUMBER_PATTERNS } from './constants';
import { useEmailShape } from './email';
import type { RequiredWhenFieldCondition } from './types/fieldCondition.types';
import { requiredWhenAll } from './utils/requiredWhenAll';

export function getStringOneOfSchema<T extends string>(
	keys: T[],
	defaultValue: T
) {
	return {
		schema: yup.string().oneOf(keys) as yup.StringSchema<T>,
		defaultValue,
	};
}

const isRequiredProps = (
	props: OptionalProps | RequiredProps
): props is RequiredProps => props.isRequired === true;

type WhenCondition = { fieldName: string; is: boolean | string };
type StreetNumberRule = boolean | { validateWhen: WhenCondition };

export const MUTUALLY_EXCLUSIVE_TEST_NAME = 'mutually-exclusive';

const REQUIRED_NOT_BLANK_TEST_NAME = 'required-not-blank';

export const requiredNotBlankTest = (message?: string) => ({
	name: REQUIRED_NOT_BLANK_TEST_NAME,
	message,
	test: (value: Maybe<string>) => value == null || value.trim().length > 0,
});

interface FieldProps {
	name: string;
	placeholder: string;
	label: string;
	wcagLabelledBy?: string;
	isDisabled?: boolean;
	isEmail?: boolean;
	isMultiline?: boolean;
	rows?: number;
	isRequiredWhen?: {
		fieldName: string;
		is: boolean | string;
		errorMessage: string;
	};
	isRequiredWhenAll?: {
		conditions: RequiredWhenFieldCondition[];
		errorMessage?: string;
	};
	defaultValue?: string;
	max?: { max: number; errorMessage: string };
	onlyNumbers?: { errorMessage: string };
	streetNumbers?: {
		isDescriptiveNumber?: StreetNumberRule;
		isOrientationNumber?: StreetNumberRule;
		isEvidenceNumber?: StreetNumberRule;
	};
	mutuallyExclusiveWith?: {
		fieldName: string;
		errorMessage: string;
		validateWhen?: WhenCondition;
	};
	message?: string;
}

interface RequiredProps extends FieldProps {
	isRequired: true;
	isRequiredMessage?: string;
}

interface OptionalProps extends FieldProps {
	isRequired?: boolean;
}

interface Output {
	defaultValue: string | null;
	field: FormInputProps;
}

interface OptionalOutput extends Output {
	schema: yup.StringSchema<Maybe<string>, AnyObject, Maybe<string>>;
}

interface RequiredOutput extends Output {
	schema: yup.StringSchema<string>;
}

export type InputDefinition = RequiredOutput | OptionalOutput;

export function useInputDefinition(props: RequiredProps): RequiredOutput;

export function useInputDefinition(props: OptionalProps): OptionalOutput;

export function useInputDefinition(
	props: OptionalProps | RequiredProps
): InputDefinition {
	const { t } = useLocale(LocalizeNameSpaceTypes.Form);
	const { getEmailShape } = useEmailShape();

	/** `isRequiredWhenAll` falls back to it, so a field that only narrows an
	 * existing `isRequired` needs no message of its own. */
	const requiredMessage =
		(isRequiredProps(props) ? props.isRequiredMessage : undefined) ??
		`${props.label} je povinné pole`;

	let schema = yup.string().nullable();
	if (isRequiredProps(props)) {
		schema = schema
			.required(requiredMessage)
			.test(requiredNotBlankTest(requiredMessage));
	} else {
		schema = schema.optional();
	}
	if (props.isEmail) {
		schema = getEmailShape({ schema });
	}

	if (props.max) {
		schema = schema.max(props.max.max, props.max.errorMessage);
	}

	if (props.onlyNumbers) {
		schema = schema.matches(/^[0-9]+$/, props.onlyNumbers.errorMessage);
	}

	const applyStreetNumberMatch = (
		rule: StreetNumberRule | undefined,
		pattern: RegExp,
		message: string
	) => {
		if (!rule) return;
		const matchOpts = { message, excludeEmptyString: true };

		if (rule === true) {
			schema = schema.matches(pattern, matchOpts);
			return;
		}

		schema = schema.when(rule.validateWhen.fieldName, {
			is: rule.validateWhen.is,
			then: (s) => s.matches(pattern, matchOpts),
		});
	};

	applyStreetNumberMatch(
		props.streetNumbers?.isDescriptiveNumber,
		STREET_NUMBER_PATTERNS.descriptive,
		t('validations.cislo-popisne-neni-ve-spravnem-tvaru')
	);

	applyStreetNumberMatch(
		props.streetNumbers?.isOrientationNumber,
		STREET_NUMBER_PATTERNS.orientation,
		t('validations.cislo-orientacni-neni-ve-spravnem-tvaru')
	);

	applyStreetNumberMatch(
		props.streetNumbers?.isEvidenceNumber,
		STREET_NUMBER_PATTERNS.evidence,
		t('validations.cislo-evidencni-neni-ve-spravnem-tvaru')
	);

	if (props.mutuallyExclusiveWith) {
		const { fieldName, errorMessage, validateWhen } =
			props.mutuallyExclusiveWith;

		const applyMutuallyExclusive = (s: typeof schema) =>
			s.test(MUTUALLY_EXCLUSIVE_TEST_NAME, errorMessage, (value, context) => {
				const sibling = context.parent?.[fieldName];
				return !(value && sibling);
			});

		schema = validateWhen
			? schema.when(validateWhen.fieldName, {
					is: validateWhen.is,
					then: applyMutuallyExclusive,
				})
			: applyMutuallyExclusive(schema);
	}

	if (props.isRequiredWhen) {
		schema = schema.when(props.isRequiredWhen.fieldName, {
			is: props.isRequiredWhen.is,
			then: (schema) =>
				schema
					.required(props.isRequiredWhen?.errorMessage)
					.test(requiredNotBlankTest(props.isRequiredWhen?.errorMessage)),
			otherwise: (schema) => schema.optional(),
		});
	}

	if (props.isRequiredWhenAll) {
		const { conditions, errorMessage } = props.isRequiredWhenAll;
		// `notRequired()` rather than `optional()`: the default value is `null` and
		// `optional()` leaves the `nonNullable` that `isRequired` brought along
		schema = requiredWhenAll(schema, conditions, (allMatch, currentSchema) =>
			allMatch
				? currentSchema
						.required(errorMessage ?? requiredMessage)
						.test(requiredNotBlankTest(errorMessage ?? requiredMessage))
				: currentSchema.notRequired()
		);
	}
	return {
		schema,
		defaultValue: props.defaultValue ?? null,
		field: {
			field: {
				name: props.name,
				placeholder: props.placeholder,
				isRequired: props?.isRequired,
				isDisabled: props.isDisabled,
				isMultiline: props.isMultiline,
				rows: props.rows,
			},
			label: {
				children: props.label,
			},
			messages: props.message
				? {
						messages: { children: props.message },
					}
				: undefined,
		},
	};
}
